/**
 * DEC-003 access-request and module dashboard access markers.
 */

import type { BackendModuleKey } from "./backendModuleKeys";

export const MODULE_ACCESS_KEYS = [
  "apartments",
  "payments",
  "budget",
  "documents",
  "tasks",
  "reminders",
  "knowledge",
  "family",
  "ai_chat",
  "integrations",
] as const;

export type ModuleAccessKey = (typeof MODULE_ACCESS_KEYS)[number];

export type AccessLevel = "none" | "full";

export type AccessTargetType = "module" | "building";

export type PendingRequestRef = {
  id: number;
  status: string;
  created_at: string;
};

export type ModuleAccessMarker = {
  access?: AccessLevel | string;
  target_type?: AccessTargetType;
  module?: ModuleAccessKey | string;
  building_id?: number;
  pending_request?: PendingRequestRef | null;
};

export type AccessRequestRecord = {
  id: number;
  requester: number;
  requester_email: string;
  requester_name: string;
  household_id: number;
  target_type: AccessTargetType;
  target_building_id: number | null;
  module_key: string;
  status: "pending" | "approved" | "denied" | "cancelled";
  message: string;
  decided_by: number | null;
  decided_by_email?: string | null;
  decided_at: string | null;
  created_at: string;
};

export type HomeDashboardCard = ModuleAccessMarker & {
  module: ModuleAccessKey;
  summary?: ApartmentsHomeSummary | PaymentsHomeSummary;
};

export type ApartmentsHomeSummary = {
  total: number;
  occupied: number;
  vacant: number;
  overdue: number;
};

export type PaymentsHomeSummary = {
  pending_count: number;
  pending_total: string;
  overdue_count: number;
  overdue_total: string;
  currency: string;
};

export type HomeDashboardResponse = {
  cards: HomeDashboardCard[];
};

export type CreateAccessRequestBody =
  | {
      target_type: "module";
      module_key: BackendModuleKey;
      message?: string;
    }
  | {
      target_type: "building";
      target_building_id: number;
      message?: string;
    };

/** Treat missing `access` as full access for older backends. */
export function resolveAccessLevel(payload: ModuleAccessMarker | null | undefined): AccessLevel {
  if (!payload) return "full";
  if (payload.access === "none") return "none";
  return "full";
}

export type AccessRequestListPage = {
  count: number;
  results: AccessRequestRecord[];
  next: string | null;
};

export function parseAccessRequestListPage(payload: unknown): AccessRequestListPage {
  if (Array.isArray(payload)) {
    const results = payload as AccessRequestRecord[];
    return { count: results.length, results, next: null };
  }
  if (payload && typeof payload === "object") {
    const page = payload as {
      count?: number;
      results?: AccessRequestRecord[];
      next?: string | null;
    };
    if (Array.isArray(page.results)) {
      return {
        count: typeof page.count === "number" ? page.count : page.results.length,
        results: page.results,
        next: page.next ?? null,
      };
    }
  }
  return { count: 0, results: [], next: null };
}

export function normalizeAccessRequestList(payload: unknown): AccessRequestRecord[] {
  return parseAccessRequestListPage(payload).results;
}

const ACCESS_REQUEST_PAGE_SIZE = 100;
export const ACCESS_REQUEST_MAX_PAGES = 50;

export function parseAccessRequestNextPage(
  next: string | null | undefined,
  currentPage: number,
): number | null {
  if (!next) return null;
  try {
    const match = String(next).match(/[?&]page=(\d+)/);
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    if (!Number.isFinite(parsed) || parsed <= currentPage) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Walk DRF pages until all records for `params` are loaded (FH-254). */
export async function fetchAllAccessRequests(
  list: (params?: Record<string, unknown>) => Promise<{ data: unknown }>,
  params: Record<string, unknown>,
): Promise<AccessRequestRecord[]> {
  const collected: AccessRequestRecord[] = [];
  let page = 1;
  let totalCount: number | null = null;

  while (page <= ACCESS_REQUEST_MAX_PAGES) {
    const res = await list({
      ...params,
      page,
      page_size: ACCESS_REQUEST_PAGE_SIZE,
    });
    const { count, results, next } = parseAccessRequestListPage(res.data);
    if (totalCount === null) {
      totalCount = count;
    }
    collected.push(...results);

    if (collected.length >= totalCount || results.length === 0) {
      break;
    }

    const nextPage = parseAccessRequestNextPage(next, page);
    if (nextPage != null) {
      page = nextPage;
      continue;
    }

    if (results.length < ACCESS_REQUEST_PAGE_SIZE) {
      break;
    }
    page += 1;
  }

  return collected;
}

export async function fetchPendingAccessRequestCount(
  list: (params?: Record<string, unknown>) => Promise<{ data: unknown }>,
): Promise<number> {
  const res = await list({ status: "pending", page: 1, page_size: 1 });
  return parseAccessRequestListPage(res.data).count;
}

export function isHouseholdApprover(role: string | undefined): boolean {
  return role === "admin";
}
